/**
 * Import: v2 cross-platform backup JSON -> CanonicalStore (full replace).
 *
 * Implements the round-trip contract (../../WEBAPP_TODO.md §0):
 *  - Detect the v2 payload (has `fuelLogs` or `userProfile`); reject anything else.
 *  - Tolerant reader: default missing arrays to [], skip bad/blank-id/orphan records,
 *    map unknown enums to defaults, never throw on one record.
 *  - Currency: resolve per-car currency, PREFETCH every rate to the display currency,
 *    then convert all amounts into it (abort untouched if a rate is missing).
 *  - Fuel/charge mirrors: import only spendings with fuelLogId == null; the rest are
 *    derived from fuelLogs downstream, never stored.
 */
import {
  type CanonicalStore,
  type Car,
  type FuelLog,
  type Spending,
  type Tyre,
  type TyreSet,
  type MountInterval,
  type Trip,
  type MaintenanceReminder,
  type PreferredService,
  type Profile,
  type SkipReport,
  CURRENT_VERSION,
} from "../model.ts";
import { parseTimestamp } from "./timestamps.ts";
import { prefetchRates, type RateProvider } from "./rates.ts";

export class InvalidBackupError extends Error {}

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);
const strOrNull = (v: unknown): string | null => (typeof v === "string" ? v : null);
const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const numOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const boolOr = (v: unknown, d: boolean): boolean => (typeof v === "boolean" ? v : d);
const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const arr = (root: Obj, key: string): unknown[] => (Array.isArray(root[key]) ? (root[key] as unknown[]) : []);
/** Finite number >= 0, else null. Guards trip telemetry against negative junk. */
const nonNegOrNull = (v: unknown): number | null => {
  const n = numOrNull(v);
  return n !== null && n >= 0 ? n : null;
};
/** Whole seconds >= 0, else null. Tolerates a float duration by truncating. */
const nonNegIntOrNull = (v: unknown): number | null => {
  const n = nonNegOrNull(v);
  return n === null ? null : Math.trunc(n);
};

export interface ParseOptions {
  /** The display currency the store will hold all amounts in. */
  displayCurrency: string;
  unitSystem?: "metric" | "imperial";
  rates: RateProvider;
}

/**
 * Best guess at the currency a backup's amounts are authored in, strongest first:
 * sourceCurrency -> userProfile.currencyCode -> first car.purchaseCurrency ->
 * most common non-blank fuelLog currency. Null if nothing usable.
 */
export function detectSourceCurrency(root: Obj): string | null {
  const sc = str(root["sourceCurrency"]);
  if (sc) return sc;
  const up = root["userProfile"];
  if (isObj(up)) {
    const cc = str(up["currencyCode"]);
    if (cc) return cc;
  }
  for (const c of arr(root, "cars")) {
    if (isObj(c)) {
      const pc = str(c["purchaseCurrency"]);
      if (pc) return pc;
    }
  }
  const counts = new Map<string, number>();
  for (const l of arr(root, "fuelLogs")) {
    if (isObj(l)) {
      const cur = str(l["currency"]);
      if (cur) counts.set(cur, (counts.get(cur) ?? 0) + 1);
    }
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [k, n] of counts) if (n > bestN) ((best = k), (bestN = n));
  return best;
}

/** Normalise `"tankEstimate":{}` / `"chargeEstimate":{}` (empty objects) to null. */
function normaliseText(text: string): string {
  return text.replace(/"(tankEstimate|chargeEstimate)"\s*:\s*\{\s*\}/g, '"$1":null');
}

export async function parseBackup(fileText: string, opts: ParseOptions): Promise<CanonicalStore> {
  let root: unknown;
  try {
    root = JSON.parse(normaliseText(fileText));
  } catch {
    throw new InvalidBackupError("Not a valid CarSpend backup file (not JSON).");
  }
  if (!isObj(root)) throw new InvalidBackupError("Not a valid CarSpend backup file.");
  // v2 cross-platform payload is detected by these keys (Android internal format is out of scope).
  if (!("fuelLogs" in root) && !("userProfile" in root)) {
    throw new InvalidBackupError("Unsupported backup format (expected a v2 cross-platform export).");
  }

  const target = opts.displayCurrency;
  const skipped: SkipReport[] = [];
  const skip = (collection: string, el: unknown, reason: string) => {
    const id = isObj(el) ? (str(el["id"]) ?? "?") : "?";
    skipped.push({ collection, id, reason });
  };

  const backupSource = detectSourceCurrency(root) ?? target;

  // ---- Per-car currency ------------------------------------------------------
  const carDeclared = new Map<string, string>();
  for (const el of arr(root, "cars")) {
    if (!isObj(el)) continue;
    const id = str(el["id"]);
    const pc = str(el["purchaseCurrency"]);
    if (id && pc) carDeclared.set(id, pc);
  }
  const carTally = new Map<string, Map<string, number>>();
  const tally = (carId: string | undefined, cur: string | undefined) => {
    if (!carId || !cur) return;
    const m = carTally.get(carId) ?? new Map<string, number>();
    m.set(cur, (m.get(cur) ?? 0) + 1);
    carTally.set(carId, m);
  };
  for (const el of arr(root, "fuelLogs")) if (isObj(el)) tally(str(el["carId"]), str(el["currency"]));
  for (const el of arr(root, "spendings")) if (isObj(el)) tally(str(el["carId"]), str(el["currency"]));
  const carCurrency = (carId: string): string => {
    const declared = carDeclared.get(carId);
    if (declared) return declared;
    const m = carTally.get(carId);
    if (m) {
      let best = "";
      let bestN = 0;
      for (const [k, n] of m) if (n > bestN) ((best = k), (bestN = n));
      if (best) return best;
    }
    return backupSource;
  };

  // ---- Prefetch every rate we could need (abort here if any is missing) ------
  const needed = new Set<string>([backupSource, target]);
  for (const c of carDeclared.values()) needed.add(c);
  for (const m of carTally.values()) for (const k of m.keys()) needed.add(k);
  for (const el of arr(root, "cars")) if (isObj(el)) { const id = str(el["id"]); if (id) needed.add(carCurrency(id)); }
  const rates = await prefetchRates(opts.rates, needed, target);
  const conv = (v: number | null | undefined, from: string): number | null => {
    if (v == null || !Number.isFinite(v)) return null;
    const r = from === target ? 1 : rates[from];
    return r == null ? v : v * r;
  };

  // ---- Cars ------------------------------------------------------------------
  const carIds = new Set<string>();
  const cars: Car[] = [];
  for (const el of arr(root, "cars")) {
    if (!isObj(el)) { skip("car", el, "unreadable"); continue; }
    const id = str(el["id"]);
    if (!id) { skip("car", el, "missing id"); continue; }
    const from = carCurrency(id);
    cars.push({
      id,
      make: str(el["make"]),
      model: str(el["model"]),
      year: num(el["year"]),
      trim: strOrNull(el["trim"]),
      licensePlate: strOrNull(el["licensePlate"]),
      vin: strOrNull(el["vin"]),
      color: strOrNull(el["color"]),
      engineType: strOrNull(el["engineType"]),
      fuelType: strOrNull(el["fuelType"]),
      manufacturerAvgConsumption: numOrNull(el["manufacturerAvgConsumption"]),
      manufacturerAvgEnergy: numOrNull(el["manufacturerAvgEnergy"]),
      batteryCapacity: numOrNull(el["batteryCapacity"]),
      tankVolume: numOrNull(el["tankVolume"]),
      tankEstimate: parseTankEstimate(el["tankEstimate"]),
      chargeEstimate: parseTankEstimate(el["chargeEstimate"]),
      purchasePrice: conv(numOrNull(el["purchasePrice"]), from),
      purchaseCurrency: target,
      purchaseDate: parseTimestamp(el["purchaseDate"]),
      purchaseOdometer: numOrNull(el["purchaseOdometer"]),
      insuranceExpiry: parseTimestamp(el["insuranceExpiry"]),
      insuranceAmount: conv(numOrNull(el["insuranceAmount"]), from),
      insuranceDocumentFilename: strOrNull(el["insuranceDocumentFilename"]),
      roadTaxExpiry: parseTimestamp(el["roadTaxExpiry"]),
      roadTaxAmount: conv(numOrNull(el["roadTaxAmount"]), from),
      roadTaxDocumentFilename: strOrNull(el["roadTaxDocumentFilename"]),
      photoFilenames: strArr(el["photoFilenames"]),
      bluetoothDeviceAddress: strOrNull(el["bluetoothDeviceAddress"]),
      autoTripRecording: boolOr(el["autoTripRecording"], false),
      vehicleType: normaliseVehicleType(str(el["vehicleType"])),
      createdAt: parseTimestamp(el["createdAt"]),
    });
    carIds.add(id);
  }

  // ---- Fuel logs -------------------------------------------------------------
  const fuelLogs: FuelLog[] = [];
  for (const el of arr(root, "fuelLogs")) {
    if (!isObj(el)) { skip("fuelLog", el, "unreadable"); continue; }
    const id = str(el["id"]);
    const carId = str(el["carId"]);
    if (!id) { skip("fuelLog", el, "missing id"); continue; }
    if (!carId || !carIds.has(carId)) { skip("fuelLog", el, "unknown car"); continue; }
    const from = str(el["currency"]) || carCurrency(carId);
    fuelLogs.push({
      id,
      carId,
      date: parseTimestamp(el["date"]),
      odometer: num(el["odometer"]) ?? 0,
      volumeAdded: num(el["volumeAdded"]) ?? 0,
      kwhAdded: numOrNull(el["kwhAdded"]),
      solarKwh: numOrNull(el["solarKwh"]),
      solarPrice: conv(numOrNull(el["solarPrice"]), from),
      totalCost: conv(num(el["totalCost"]) ?? 0, from) ?? 0,
      fuelCost: conv(numOrNull(el["fuelCost"]), from),
      energyCost: conv(numOrNull(el["energyCost"]), from),
      currency: target,
      fuelGrade: str(el["fuelGrade"]) ?? "Regular 95",
      isFullFill: boolOr(el["isFullFill"], true),
      stationName: strOrNull(el["stationName"]),
      notes: strOrNull(el["notes"]),
      avgSpeed: numOrNull(el["avgSpeed"]),
      photoFilenames: strArr(el["photoFilenames"]),
      documentFilenames: strArr(el["documentFilenames"]),
    });
  }

  // ---- Spendings (MANUAL only; mirrors dropped and rebuilt downstream) -------
  const spendings: Spending[] = [];
  for (const el of arr(root, "spendings")) {
    if (!isObj(el)) { skip("spending", el, "unreadable"); continue; }
    const fuelLogId = str(el["fuelLogId"]);
    if (fuelLogId) continue; // mirror of a fuel/charge log — rebuilt from fuelLogs
    const id = str(el["id"]);
    const carId = str(el["carId"]);
    if (!id) { skip("spending", el, "missing id"); continue; }
    if (!carId || !carIds.has(carId)) { skip("spending", el, "unknown car"); continue; }
    const from = str(el["currency"]) || carCurrency(carId);
    spendings.push({
      id,
      carId,
      amount: conv(num(el["amount"]) ?? 0, from) ?? 0,
      category: str(el["category"]) ?? "Other",
      date: parseTimestamp(el["date"]),
      notes: strOrNull(el["notes"]),
      currency: target,
      fuelLogId: null,
      photoFilenames: strArr(el["photoFilenames"]),
      documentFilenames: strArr(el["documentFilenames"]),
    });
  }

  // ---- Tyres / tyre sets -----------------------------------------------------
  const tyres: Tyre[] = [];
  for (const el of arr(root, "tyres")) {
    if (!isObj(el)) { skip("tyre", el, "unreadable"); continue; }
    const id = str(el["id"]);
    const carId = str(el["carId"]);
    if (!id) { skip("tyre", el, "missing id"); continue; }
    if (!carId || !carIds.has(carId)) { skip("tyre", el, "unknown car"); continue; }
    const from = carCurrency(carId);
    tyres.push({
      id,
      carId,
      position: str(el["position"]) ?? "Front Left",
      season: str(el["season"]) ?? "All Seasons",
      brand: str(el["brand"]),
      model: str(el["model"]),
      size: str(el["size"]),
      purchaseDate: parseTimestamp(el["purchaseDate"]),
      purchasePrice: conv(numOrNull(el["purchasePrice"]), from),
      purchaseOdometer: numOrNull(el["purchaseOdometer"]),
      installDate: parseTimestamp(el["installDate"]),
      installOdometer: numOrNull(el["installOdometer"]),
      treadDepth: numOrNull(el["treadDepth"]),
      pressure: numOrNull(el["pressure"]),
      dotCode: strOrNull(el["dotCode"]),
      notes: strOrNull(el["notes"]),
      photoFilenames: strArr(el["photoFilenames"]),
      documentFilenames: strArr(el["documentFilenames"]),
      spendingId: strOrNull(el["spendingId"]),
      createdAt: parseTimestamp(el["createdAt"]),
    });
  }

  const tyreSets: TyreSet[] = [];
  for (const el of arr(root, "tyreSets")) {
    if (!isObj(el)) { skip("tyreSet", el, "unreadable"); continue; }
    const id = str(el["id"]);
    const carId = str(el["carId"]);
    if (!id) { skip("tyreSet", el, "missing id"); continue; }
    if (!carId || !carIds.has(carId)) { skip("tyreSet", el, "unknown car"); continue; }
    const from = carCurrency(carId);
    tyreSets.push({
      id,
      carId,
      name: str(el["name"]),
      season: str(el["season"]) ?? "All Seasons",
      brand: str(el["brand"]),
      model: str(el["model"]),
      size: str(el["size"]),
      purchasePrice: conv(numOrNull(el["purchasePrice"]), from),
      purchaseDate: parseTimestamp(el["purchaseDate"]),
      purchaseOdometer: numOrNull(el["purchaseOdometer"]),
      initialTreadDepth: numOrNull(el["initialTreadDepth"]),
      currentTreadDepth: numOrNull(el["currentTreadDepth"]),
      mountLog: parseMountLog(el["mountLog"]),
      spendingId: strOrNull(el["spendingId"]),
      countPriceInSpend: el["countPriceInSpend"] === true,
      notes: strOrNull(el["notes"]),
      createdAt: parseTimestamp(el["createdAt"]),
    });
  }

  // ---- Trips -----------------------------------------------------------------
  const trips: Trip[] = [];
  for (const el of arr(root, "trips")) {
    if (!isObj(el)) { skip("trip", el, "unreadable"); continue; }
    const id = str(el["id"]);
    const carId = str(el["carId"]);
    if (!id) { skip("trip", el, "missing id"); continue; }
    if (!carId || !carIds.has(carId)) { skip("trip", el, "unknown car"); continue; }
    const from = carCurrency(carId);
    trips.push({
      id,
      carId,
      date: parseTimestamp(el["date"]),
      startOdometer: num(el["startOdometer"]) ?? 0,
      endOdometer: num(el["endOdometer"]) ?? 0,
      notes: strOrNull(el["notes"]),
      startLocationName: strOrNull(el["startLocationName"]),
      endLocationName: strOrNull(el["endLocationName"]),
      fuelVolume: numOrNull(el["fuelVolume"]),
      fuelEnergy: numOrNull(el["fuelEnergy"]),
      fuelCost: conv(numOrNull(el["fuelCost"]), from),
      fuelStation: strOrNull(el["fuelStation"]),
      tollCost: conv(numOrNull(el["tollCost"]), from),
      parkingCost: conv(numOrNull(el["parkingCost"]), from),
      passengers: numOrNull(el["passengers"]),
      startLatitude: numOrNull(el["startLatitude"]),
      startLongitude: numOrNull(el["startLongitude"]),
      endLatitude: numOrNull(el["endLatitude"]),
      endLongitude: numOrNull(el["endLongitude"]),
      waypoints: strArr(el["waypoints"]),
      polyline: strArr(el["polyline"]),
      purpose: normalisePurpose(str(el["purpose"])),
      mileageRate: numOrNull(el["mileageRate"]),
      fuelLogIds: strArr(el["fuelLogIds"]),
      // Author's display units — deliberately NOT currency/unit converted.
      avgConsumptionOverride: numOrNull(el["avgConsumptionOverride"]),
      // Telemetry is metric on the wire; negatives are nonsense, so drop them.
      durationSec: nonNegIntOrNull(el["durationSec"]),
      avgSpeedKmh: nonNegOrNull(el["avgSpeedKmh"]),
      topSpeedKmh: nonNegOrNull(el["topSpeedKmh"]),
    });
  }

  // ---- Maintenance reminders -------------------------------------------------
  const maintenanceReminders: MaintenanceReminder[] = [];
  for (const el of arr(root, "maintenanceReminders")) {
    if (!isObj(el)) { skip("reminder", el, "unreadable"); continue; }
    const id = str(el["id"]);
    const carId = str(el["carId"]);
    if (!id) { skip("reminder", el, "missing id"); continue; }
    if (!carId || !carIds.has(carId)) { skip("reminder", el, "unknown car"); continue; }
    maintenanceReminders.push({
      id,
      carId,
      title: str(el["title"]),
      notes: strOrNull(el["notes"]),
      reminderType: str(el["reminderType"]) ?? "custom",
      dueDate: parseTimestamp(el["dueDate"]),
      dueOdometer: numOrNull(el["dueOdometer"]),
      notifyBeforeDays: numOrNull(el["notifyBeforeDays"]),
      notifyBeforeDistance: numOrNull(el["notifyBeforeDistance"]),
      isCompleted: boolOr(el["isCompleted"], false),
      recursEveryDays: numOrNull(el["recursEveryDays"]),
      recursEveryDistance: numOrNull(el["recursEveryDistance"]),
      createdAt: parseTimestamp(el["createdAt"]),
    });
  }

  // ---- Preferred services (owner-scoped; ownership remapped locally) ---------
  const services: PreferredService[] = [];
  for (const el of arr(root, "services")) {
    if (!isObj(el)) { skip("service", el, "unreadable"); continue; }
    const id = str(el["id"]);
    if (!id) { skip("service", el, "missing id"); continue; }
    services.push({
      id,
      name: str(el["name"]),
      category: strOrNull(el["category"]),
      address: strOrNull(el["address"]),
      phone: strOrNull(el["phone"]),
      email: strOrNull(el["email"]),
      notes: strOrNull(el["notes"]),
      createdAt: parseTimestamp(el["createdAt"]),
    });
  }

  const version = num(root["version"]) ?? CURRENT_VERSION;
  const profile: Profile = {
    displayCurrency: target,
    unitSystem: opts.unitSystem ?? "metric",
    mileageRate: readProfileMileageRate(root),
  };

  // Drop tyre / tyre-set mirror spendings. The phones store a "Tires" spending for
  // each tyre or set that has a price (linked via spendingId); the ledger rebuilds
  // that cost from tyres/tyreSets, so keeping the imported mirror double-counts.
  // (Fuel/charge mirrors were already dropped above via fuelLogId.)
  const tyreMirrorSpendingIds = new Set<string>();
  for (const t of tyres) if (t.spendingId) tyreMirrorSpendingIds.add(t.spendingId);
  for (const ts of tyreSets) if (ts.spendingId) tyreMirrorSpendingIds.add(ts.spendingId);
  const manualSpendings = spendings.filter((s) => !tyreMirrorSpendingIds.has(s.id));

  return {
    schemaVersion: version,
    profile,
    cars,
    fuelLogs,
    spendings: manualSpendings,
    tyres,
    tyreSets,
    trips,
    maintenanceReminders,
    services,
    skipped,
  };
}

function parseTankEstimate(v: unknown): import("../model.js").TankEstimate | null {
  if (!isObj(v)) return null;
  return {
    fullRange: num(v["fullRange"]),
    rangeLeft: num(v["rangeLeft"]),
    percent: num(v["percent"]),
    volume: num(v["volume"]),
    savedAt: parseTimestamp(v["savedAt"]),
  };
}

function parseMountLog(v: unknown): MountInterval[] {
  if (!Array.isArray(v)) return [];
  return v.filter(isObj).map((m) => ({
    id: str(m["id"]),
    mountedOdometer: num(m["mountedOdometer"]) ?? 0,
    mountedDate: parseTimestamp(m["mountedDate"]),
    removedOdometer: numOrNull(m["removedOdometer"]),
    removedDate: parseTimestamp(m["removedDate"]),
  }));
}

function normaliseVehicleType(v: string | undefined): string {
  const t = (v ?? "").toLowerCase();
  return t === "motorcycle" || t === "van" || t === "other" ? t : "car";
}

function normalisePurpose(v: string | undefined): string | null {
  if (v === "Business" || v === "Personal" || v === "Medical") return v;
  return null;
}

function readProfileMileageRate(root: Obj): number | null {
  const up = root["userProfile"];
  return isObj(up) ? numOrNull(up["mileageRate"]) : null;
}
